/* One camp or trip: /trips/<slug>
   ----------------------------------------------------------------------
   The same page for every item in src/data/camps.js except Dubai, which
   keeps its own page. Redesigned 2026-10-01 on the owner's note "enhance
   it a bit more from inside". Top to bottom:
     full-width photo hero (back link, kind and country, title, tagline,
     status, buttons)
     → key facts in one bar over the hero's lower edge
     → the story in one readable column: what it is, the educational
       side, the results (only where a camp has run), highlights or what
       students develop, the programme, two more photos, day by day,
       supervision, what is included, notes
     → safety → how to book → enquiry form (destination preselected)
     → three other camps and trips.
   A floating button reaches the form from anywhere on the page. */

import { SIZES } from "../../lib/img";
import React from "react";
import SEO from "../../components/SEO";
import SiteHeader from "../../components/SiteHeader";
import LocaleLink from "../../components/LocaleLink";
import { useLocale, useL } from "../../i18n/LocaleContext";
import { CAMP_BY_SLUG, CAMPS_COPY, CAMP_KINDS, FACT_LABELS, FEATURED_CAMP, ALL_TRIPS } from "../../data/camps";
import { tripSchema, breadcrumbSchema } from "../../data/schemas";
import {
  ARROW, Pic, FactBar, CheckList, ThemeList, DayList, NoteBlock, FloatingCta, useHashScroll,
} from "../../components/catalog/CatalogParts";
import { CampGrid, ResultsBand, SafetySection, BookingSection, EnquirySection, TextLink } from "./TripBlocks";
import { track } from "../../lib/analytics";

/* Three other items for the foot of the page: the next three in index
   order, wrapping around, never the page's own trip. */
function otherCamps(slug) {
  const all = [FEATURED_CAMP, ...ALL_TRIPS];
  const i = all.findIndex((c) => c.slug === slug);
  return [1, 2, 3].map((k) => all[(i + k) % all.length]);
}

export default function CampPage({ slug }) {
  const { locale } = useLocale();
  const raw = CAMP_BY_SLUG[slug];
  const c = useL(raw);
  const copy = useL(CAMPS_COPY);
  const kinds = useL(CAMP_KINDS);
  const labels = useL(FACT_LABELS);
  const d = copy.detail;
  useHashScroll();

  const factRows = (c.facts || []).map((f) => ({ label: labels[f.k], value: f.v }));
  const [hero, ...more] = c.photos;

  return (
    <div className="cat-page">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        path={raw.href}
        image={`${raw.photos[0].src}.jpg`}
        schema={[
          tripSchema(raw, locale),
          breadcrumbSchema([
            { name: CAMPS_COPY.home, path: "/" },
            { name: CAMPS_COPY.eyebrow, path: "/trips" },
            { name: raw.title, path: raw.href },
          ], locale),
        ]}
      />
      <SiteHeader ctaHref="#enquire" />
      <main id="main">
        {/* ── Hero: the destination, full width (the UK camp shows its
            2026 campers instead, see src/data/camps.js) ── */}
        <header className={`cat-trip-hero${raw.peopleHero ? " cat-trip-hero--people" : ""}`}>
          <Pic src={hero.src} alt={hero.alt} pos={hero.pos} eager className="cat-trip-hero-bg" sizes={SIZES.full} />
          <div className="cat-trip-hero-shade" aria-hidden="true" />
          <div className="cat-trip-hero-inner">
            <LocaleLink to="/trips" className="cat-crumb">{ARROW}{d.backToAll}</LocaleLink>
            <p className="cat-eyebrow">{kinds[raw.kind]} · {c.country}</p>
            <h1 className="cat-title">{c.title}</h1>
            <p className="cat-tagline">{c.tagline}</p>
            <p className="cat-pills">
              <span className={`cat-pill cat-pill--${raw.status.tone}`}>{c.status.label}</span>
              {c.next && <span className="cat-pill">{c.next}</span>}
              {raw.sample && <span className="cat-pill cat-pill--sample">{d.sampleBadge}</span>}
            </p>
            <div className="cat-actions cat-hero-actions">
              <a href="#enquire" className="btn btn-gold-lg" onClick={() => track("trip_cta_click", { trip: slug, placement: "hero" })}>
                {c.cta.label}
              </a>
              <a
                href={`https://wa.me/${CAMPS_COPY.contact.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-white"
                onClick={() => track("trip_whatsapp_click", { trip: slug })}
              >
                {copy.contact.whatsappLabel}
              </a>
            </div>
          </div>
        </header>

        {factRows.length > 0 && (
          <div className="cat-factbar-wrap">
            <FactBar rows={factRows} label={d.atAGlance} />
          </div>
        )}


        {/* ── The story, in one readable column ── */}
        <div className="cat-story">
          {c.what && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{c.what.title}</h2>
              {c.what.paragraphs.map((p, i) => <p key={i} className="cat-lead-text">{p}</p>)}
            </section>
          )}

          {c.education && <NoteBlock title={d.education}>{c.education}</NoteBlock>}

          {c.results && <ResultsBand results={c.results} eyebrow={c.results.eyebrow} />}

          {c.highlights && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{c.highlights.title}</h2>
              <CheckList items={c.highlights.items} columns />
            </section>
          )}
          {c.develop && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{c.develop.title}</h2>
              <ul className="cat-chips">{c.develop.chips.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>
          )}
          {c.themes && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{d.themes}</h2>
              <ThemeList items={c.themes} />
            </section>
          )}

          {more.length >= 2 && (
            <div className="cat-photo-pair">
              {more.slice(0, 2).map((p) => <Pic key={p.src} src={p.src} alt={p.alt} sizes={SIZES.half} />)}
            </div>
          )}

          {c.gallery && (
            <section className="cat-story-block" aria-labelledby="gallery-title">
              <h2 id="gallery-title" className="cat-block-title">{c.gallery.title}</h2>
              <ul className="cat-gallery">
                {c.gallery.photos.map((p) => <li key={p.src}><Pic src={p.src} alt={p.alt} w={900} h={1200} sizes={SIZES.tile} /></li>)}
              </ul>
            </section>
          )}

          {c.days && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{d.days}</h2>
              <DayList days={c.days} dayWord={d.dayWord} />
            </section>
          )}
          {c.supervision && (
            <section className="cat-story-block">
              <h2 className="cat-block-title">{c.supervision.title}</h2>
              <p className="cat-text">{c.supervision.body}</p>
            </section>
          )}

          {c.included && (
            <section className="cat-panel cat-panel--accent">
              <h2 className="cat-panel-title">{d.included}</h2>
              <CheckList items={c.included} columns />
              {c.notIncluded && <p className="cat-muted">{c.notIncluded}</p>}
            </section>
          )}
          {c.takeHome && (
            <section className="cat-panel cat-panel--accent">
              <h2 className="cat-panel-title">{c.takeHome.title}</h2>
              <CheckList items={c.takeHome.items} columns />
            </section>
          )}

          {(raw.sample || c.note || c.pastHref) && (
            <div className="cat-story-notes">
              {raw.sample && <p className="cat-note">{d.sampleNote}</p>}
              {c.note && <p className="cat-note">{c.note}</p>}
              {c.pastHref && <p className="cat-text"><TextLink to={c.pastHref}>{c.pastLabel}</TextLink></p>}
            </div>
          )}
        </div>

        <SafetySection />
        <BookingSection />
        <EnquirySection defaultValue={slug} />

        {/* ── Other camps and trips ── */}
        <section className="cat-section" aria-labelledby="others-title">
          <div className="cat-wrap">
            <h2 id="others-title" className="cat-h2 cat-h2--gap">{d.otherTrips}</h2>
            <CampGrid camps={otherCamps(slug)} />
          </div>
        </section>
      </main>
      <FloatingCta
        href="#enquire"
        label={c.cta.label}
        heroSelector=".cat-hero-actions"
        targetId="enquire"
        event="trip_cta_click"
        params={{ trip: slug, placement: "float" }}
      />
    </div>
  );
}
